import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Cześć - jestem asystentem Veliory. Zapytaj o nasze usługi, proces współpracy lub o to, jak strona, chatbot i automatyzacje mogą pomóc Twojej firmie." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.session_id) setSessionId(data.session_id);
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Przepraszam - nie udało się połączyć. Spróbuj ponownie." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: "spring", stiffness: 200 }}
        className="fixed bottom-6 right-6 z-[110] h-14 w-14 rounded-full glass-dark flex items-center justify-center text-white shadow-2xl hover:-translate-y-1 transition-transform"
        aria-label="Open chat assistant"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><X size={22} /></motion.span>
                : <motion.span key="s" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }} style={{ color: "var(--gold)" }}><Sparkles size={22} /></motion.span>}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[110] w-[92vw] max-w-[380px] h-[520px] max-h-[70vh] glass-dark rounded-3xl overflow-hidden flex flex-col text-white"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <span className="h-2 w-2 rounded-full" style={{ background: "var(--gold)" }} />
              <div>
                <p className="text-sm font-semibold leading-none">Asystent Veliora</p>
                <p className="text-[11px] text-white/50 mt-1">Zwykle odpowiada natychmiast</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-white text-black rounded-br-sm" : "bg-white/8 text-white/90 rounded-bl-sm border border-white/10"}`}
                  >
                    {m.content || <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-white/50 animate-bounce" /><span className="h-1.5 w-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:0.15s]" /><span className="h-1.5 w-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:0.3s]" /></span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2 bg-white/5 rounded-full pl-4 pr-2 py-1.5 border border-white/10">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Zapytaj o cokolwiek…"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40"
                />
                <button data-testid="chat-send" onClick={send} disabled={busy} className="h-8 w-8 rounded-full bg-white text-black flex items-center justify-center disabled:opacity-40 transition-opacity">
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
